// §IV claim_cards.where_it_stands_now — the render side of the SHARED currentStanding
// detector (engine: src/insights/render_keys.js, contract s323.d1/s390.d4). The engine
// emits the structural facts; this file selects the canonical §IV phrase. It is the §IV
// REGISTER, a sibling of standing.js (§III) — same engine input, different words:
//
//   • §IV register: the low pole is "sit at the floor" (§III: "converge to the same low");
//     mid is shared verbatim ("sit in the middle together"); high is shared ("sit high").
//   • §IV has NO forward clause — "that separation is the patterns chapter." rides only in
//     §III. In §IV every state ends on the position, full stop.
//   • a single broken-out layer is NAMED (amber); two or more are COUNTED (position only).
//
// Contract in:  { touchedCount:T, cloud:{size:M, level:'low'|'mid'|'high', band}|null,
//                 outlier|null, outliers:[{layer, value, dir:'above'|'below'}] }
// Result out:   { state, segments, text } — segments carry the amber layer name so
//               SectionIV can wrap it in <span class="lp-note-amber">; text is the plain
//               concatenation (tests / aria). null → the card is dropped by the caller.
//
// The engine never emits a `split` field (CD's canon standsNow used one as a shortcut for
// state 7); here state 7 is derived structurally — a low/high cloud with 2+ layers broken
// out the OTHER way — exactly as §III's standing.js does. Same rendered string, honest input.

import { LAYERS } from './layersConfig.js';
import { STANDING } from '../copy/passportCopy.js';

const NUM_WORD = {
  0: 'zero', 1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 7: 'seven',
  8: 'eight', 9: 'nine', 10: 'ten', 11: 'eleven', 12: 'twelve', 13: 'thirteen', 14: 'fourteen', 15: 'fifteen',
};
const word = (n) => NUM_WORD[n] ?? String(n);
const layerName = (key) => LAYERS.find((L) => L.key === key)?.name ?? key;

// normalise string parts to {text}; compute plain text + state.
function make(state, parts) {
  const segments = parts.map((p) => (typeof p === 'string' ? { text: p } : p));
  return { state, segments, text: segments.map((s) => s.text).join('') };
}

export function standingIV(cs) {
  // hard fallback: 0 touched layers → card omitted by the caller
  if (!cs || cs.touchedCount === 0) return null;
  const T = word(cs.touchedCount);

  if (!cs.cloud) return make('scatter', [STANDING.scatter()]); // (5)

  const { level, size } = cs.cloud;
  const outs = cs.outliers ?? [];
  const OTH = word(Math.max(cs.touchedCount - 1, 0));

  // (7) low/high cloud + 2+ layers broken out the OTHER way → two groups, counted.
  // Ordering (s407.d1): larger group leads; tie → the high clause leads. §IV register:
  // the low pole is "sit at the floor". No forward clause.
  if ((level === 'low' || level === 'high') && outs.length >= 2) {
    const highSize = level === 'high' ? size : outs.length;
    const lowSize = level === 'low' ? size : outs.length;
    return make(
      'two-sided',
      highSize >= lowSize
        ? [STANDING.twoSidedHighFirst(word(highSize), word(lowSize))]
        : [STANDING.twoSidedLowFirst(word(lowSize), word(highSize))],
    );
  }

  // (1/2) low/high cloud + exactly one broken-out layer (opposite side, fold rule) → named
  if (outs.length === 1 && (level === 'low' || level === 'high')) {
    const name = { text: layerName(outs[0].layer), amber: true };
    if (outs[0].dir === 'above' && level === 'low')
      return make('one-outlier', [name, STANDING.oneAboveLow(OTH)]); // (1)
    if (outs[0].dir === 'below' && level === 'high')
      return make('one-outlier', [name, STANDING.oneBelowHigh(OTH)]); // (2)
  }

  // (3/4) whole touched set together at the low / high pole. Mirror of state (6): when
  // NOTHING stands apart (outs.length === 0) the touched set is together at the pole, and
  // the subject is the whole touched count T — exactly as (6) does for mid. The earlier
  // `size === cs.touchedCount` guard was over-strict: a low/high cloud of 5 with two more
  // layers near-but-not-in the band (gap < 2, so not outliers) left size < T and fell
  // through to the [TODO-COPY] net, leaking a raw placeholder to the user for a perfectly
  // valid current-stage vector (qa-reviewer §IV, 2026-08-03). Dropping the guard reuses the
  // already-ratified state-3/4 strings; NO new copy is invented.
  if (outs.length === 0) {
    if (level === 'low') return make('all-together', [STANDING.allLow(T)]); // (3)
    if (level === 'high') return make('all-together', [STANDING.allHigh(T)]); // (4)
  }

  // (6) mid cloud, nothing broken out — subject is the whole touched set (T, not cloud size)
  if (level === 'mid' && outs.length === 0) return make('all-together', [STANDING.allMid(T)]);

  // (8) mid cloud + broken-out layers. One → named (8a/8b); two same side → counted (8c/8d);
  // one up + one down → 8e. "others" = touched − outliers, not cloud.size.
  if (outs.length && level === 'mid') {
    const REST = word(Math.max(cs.touchedCount - outs.length, 0));
    const up = outs.filter((o) => o.dir === 'above').length;
    const dn = outs.filter((o) => o.dir === 'below').length;
    if (outs.length === 1) {
      const name = { text: layerName(outs[0].layer), amber: true };
      return make('mid-outlier', [name, STANDING.midOne(up ? STANDING.side.up : STANDING.side.down, OTH)]); // (8a/8b)
    }
    if (up && dn) return make('mid-outlier', [STANDING.midSplit(REST)]); // (8e)
    return make('mid-outlier', [STANDING.midSame(word(outs.length), up ? STANDING.side.up : STANDING.side.down, REST)]); // (8c/8d)
  }

  // structural fact outside the spec's states — surfaced, never silently dropped (see §III)
  return make('undefined', [`[TODO-COPY] detector branch outside spec states: cloud ${size} · ${level}${outs.length ? ` + outliers ${outs.map((o) => o.layer + ' ' + o.dir).join(', ')}` : ', no outlier'}`]);
}
